/**
 * ============================================================================
 * CLIENTS STORE ACTIONS
 * ============================================================================
 */

import * as ACTION_TYPES from './action-types'
import * as MUTATIONS from './mutation-types'
import * as TABLE_MUTATIONS from '@/store/modules/Table/mutation-types'

// Models
import { createClient } from '@models/Client'

// Generic Apollo helpers
import {
	executeSimpleQuery,
	executeMutation,
	executeQueryWithLoading
} from '@/store/helpers/apollo'

// Clients-specific helpers
import {
	buildClientsQueryPayload,
	buildClientOrdersPayload,
	buildColumnsFromTable,
	buildSearchPayload
} from '@/store/helpers/clients'

// GraphQL imports
import FetchClientsSearchSuggestions from './Schema/Queries/FetchClientsSearchSuggestions.gql'
import FetchClients from './Schema/Queries/FetchClients.gql'
import FetchClientsMeta from './Schema/Queries/FetchClientsMeta.gql'
import FetchClient from './Schema/Queries/FetchClient.gql'
import FetchClientOrders from './Schema/Queries/FetchClientOrders.gql'
import FetchClientOrdersOld from './Schema/Queries/FetchClientOrdersOld.gql'
import FetchClientOrdersMeta from './Schema/Queries/FetchClientOrdersMeta.gql'
import FetchClientOrdersProducts from './Schema/Queries/FetchClientOrdersProducts.gql'
import FetchClientOrdersProductsMeta from './Schema/Queries/FetchClientOrdersProductsMeta.gql'
import FetchClientsStatistics from './Schema/Queries/FetchClientsStatistics.gql'
import FetchCreditLogs from './Schema/Queries/FetchCreditLogs.gql'
import ClientDeposit from './Schema/Mutations/ClientDeposit.gql'
import AddClient from './Schema/Mutations/AddClient.gql'
import EditClient from './Schema/Mutations/EditClient.gql'
import DeleteClient from './Schema/Mutations/DeleteClient.gql'

export default {
	/**
     * ========================================================================
     * CLIENTS LISTING
     * ========================================================================
     */

	/**
     * Fetch clients
     */
	async [ACTION_TYPES.FETCH_CLIENTS] (
		{ commit, state, rootState },
		{ department, page = 1, size = 20, payload = {} } = {}
	) {
		const columns = buildColumnsFromTable(state.Table)
		const queryPayload = buildClientsQueryPayload(
			state.searchFilters,
			payload,
			department || rootState.Auth.user.selectedDepartmentId
		)

		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'clients',
			query: {
				query: FetchClients,
				variables: {
					payload: queryPayload,
					page,
					size,
					...columns
				}
			},
			onSuccess: (data) => {
				const clients = data.clientsNew.map(client => createClient(client))
				const variables = Object.keys(payload).length
					? Object.assign({}, state.searchFilters, payload)
					: { ...state.searchFilters }

				commit(MUTATIONS.SET_SEARCH_FILTERS, variables)
				commit(`Table/${TABLE_MUTATIONS.SET_DATA}`, clients)
				commit(MUTATIONS.SET_CLIENTS, clients)
				return clients
			}
		})
	},

	/**
     * Fetch clients meta
     */
	async [ACTION_TYPES.FETCH_CLIENTS_META] (
		{ commit, state, rootState },
		{ department, page = 1, size = 20, payload = {} } = {}
	) {
		const queryPayload = buildClientsQueryPayload(
			state.searchFilters,
			payload,
			department || rootState.Auth.user.selectedDepartmentId
		)

		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'meta',
			query: {
				query: FetchClientsMeta,
				variables: {
					payload: queryPayload,
					page,
					size
				}
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_META, data.clientsNewMeta)
				return data.clientsNewMeta
			}
		})
	},

	/**
     * Fetch clients statistics
     */
	async [ACTION_TYPES.FETCH_CLIENTS_STATISTICS] (
		{ commit, state, rootState },
		{ department, payload = {} } = {}
	) {
		const queryPayload = buildClientsQueryPayload(
			state.searchFilters,
			payload,
			department || rootState.Auth.user.selectedDepartmentId
		)

		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'clientsStatistics',
			query: {
				query: FetchClientsStatistics,
				variables: { payload: queryPayload }
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_CLIENTS_STATISTICS, data.clientsStatistics)
				return data.clientsStatistics
			}
		})
	},

	/**
     * ========================================================================
     * SINGLE CLIENT OPERATIONS
     * ========================================================================
     */

	/**
     * Fetch single client
     */
	async [ACTION_TYPES.FETCH_CLIENT] ({ commit }, id) {
		const data = await executeSimpleQuery({
			query: FetchClient,
			variables: { id }
		})

		const client = createClient(data.clientNew)
		commit(MUTATIONS.SET_CLIENT, client)
		return client
	},

	/**
     * Fetch client statistics
     */
	async [ACTION_TYPES.FETCH_CLIENT_STATISTICS] (
		{ commit, state },
		payload = {}
	) {
		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'clientStatistics',
			query: {
				query: FetchClientsStatistics,
				variables: {
					payload: {
						...payload,
						ids: [state.client.id],
						departmentIds: [state.client.departmentId]
					}
				}
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_CLIENT_STATISTICS, data.clientsStatistics)
				return data.clientsStatistics
			}
		})
	},

	/**
     * Add client
     */
	async [ACTION_TYPES.ADD_CLIENT] (_, payload) {
		return executeMutation({
			mutation: {
				mutation: AddClient,
				variables: { payload }
			},
			onSuccess: (data) => createClient(data.createClientNew)
		})
	},

	/**
     * Edit client
     */
	async [ACTION_TYPES.EDIT_CLIENT] ({ commit, state }, payload) {
		return executeMutation({
			mutation: {
				mutation: EditClient,
				variables: { payload }
			},
			onSuccess: (data) => {
				if (state.client && state.client.id === payload.id) {
					commit(MUTATIONS.SET_CLIENT, createClient({ ...state.client, ...payload }))
				}
				return data
			}
		})
	},

	/**
     * Delete client
     */
	async [ACTION_TYPES.DELETE_CLIENT] (_, payload) {
		return executeMutation({
			mutation: {
				mutation: DeleteClient,
				variables: { payload }
			},
			onSuccess: (data) => data.status
		})
	},

	/**
     * ========================================================================
     * CLIENT FINANCIAL OPERATIONS
     * ========================================================================
     */

	/**
     * Deposit client
     */
	async [ACTION_TYPES.DEPOSIT_CLIENT] (_, payload) {
		return executeMutation({
			mutation: {
				mutation: ClientDeposit,
				variables: { payload }
			},
			onSuccess: (data) => data.clientDeposit
		})
	},

	/**
     * Fetch credit logs
     */
	async [ACTION_TYPES.FETCH_CREDIT_LOGS] (_, payload) {
		const data = await executeSimpleQuery({
			query: FetchCreditLogs,
			variables: {
				payload,
				page: 1,
				size: 99999
			}
		})
		return data.creditLogs
	},

	/**
     * ========================================================================
     * CLIENT ORDERS
     * ========================================================================
     */

	/**
     * Fetch client orders (old)
     */
	async [ACTION_TYPES.FETCH_CLIENT_ORDERS_OLD] (
		{ commit },
		{ clientIds = [], page = 1, size = 20 } = {}
	) {
		const data = await executeSimpleQuery({
			query: FetchClientOrdersOld,
			variables: {
				payload: { clientIds },
				page,
				size
			}
		})

		commit(MUTATIONS.SET_CLIENT_ORDERS_OLD_META, data.orders.meta)
		commit(MUTATIONS.SET_CLIENT_ORDERS_OLD, data.orders.nodes)
		return data.orders.nodes
	},

	/**
     * Fetch client orders
     */
	async [ACTION_TYPES.FETCH_CLIENT_ORDERS] (
		{ state },
		{ clientIds = [], page = 1, size = 20, ...payload } = {}
	) {
		const queryPayload = buildClientOrdersPayload(
			state.ordersSearchFilters,
			{ clientIds, ...payload },
			page,
			size
		)

		const data = await executeSimpleQuery({
			query: FetchClientOrders,
			variables: queryPayload
		})
		return data.ordersNew
	},

	/**
     * Fetch client orders meta
     */
	async [ACTION_TYPES.FETCH_CLIENT_ORDERS_META] (
		{ commit, state },
		{ clientIds = [], page = 1, size = 20, ...payload } = {}
	) {
		const queryPayload = buildClientOrdersPayload(
			state.ordersSearchFilters,
			{ clientIds, ...payload },
			page,
			size
		)

		const data = await executeSimpleQuery({
			query: FetchClientOrdersMeta,
			variables: queryPayload
		})

		commit(MUTATIONS.SET_CLIENT_ORDERS_META, data.ordersMetaNew)
		return data.ordersMetaNew
	},

	/**
     * ========================================================================
     * CLIENT ORDERS PRODUCTS
     * ========================================================================
     */

	/**
     * Fetch client orders products
     */
	async [ACTION_TYPES.FETCH_CLIENT_ORDERS_PRODUCTS] (
		{ state },
		{ clientIds = [], page = 1, size = 20, ...payload } = {}
	) {
		const columns = buildColumnsFromTable(state.ClientOrdersProductsTable)
		const queryPayload = buildClientOrdersPayload(
			state.ordersSearchFilters,
			{ clientIds, productQueryType: 'order', ...payload },
			page,
			size
		)

		const data = await executeSimpleQuery({
			query: FetchClientOrdersProducts,
			fetchPolicy: 'no-cache',
			variables: {
				...queryPayload,
				...columns
			}
		})
		return data.orderProductsNew
	},

	/**
     * Fetch client orders products meta
     */
	async [ACTION_TYPES.FETCH_CLIENT_ORDERS_PRODUCTS_META] (
		{ commit, state },
		{ clientIds = [], page = 1, size = 20, ...payload } = {}
	) {
		const queryPayload = buildClientOrdersPayload(
			state.ordersSearchFilters,
			{ clientIds, productQueryType: 'order', ...payload },
			page,
			size
		)

		const data = await executeSimpleQuery({
			query: FetchClientOrdersProductsMeta,
			variables: queryPayload
		})

		commit(MUTATIONS.SET_CLIENT_ORDERS_PRODUCTS_META, data.orderProductsMetaNew)
		return data.orderProductsMetaNew
	},

	/**
     * ========================================================================
     * SEARCH & SUGGESTIONS
     * ========================================================================
     */

	/**
     * Fetch clients search suggestions
     */
	async [ACTION_TYPES.FETCH_CLIENTS_SEARCH_SUGGESTIONS] (
		{ rootState },
		{ searchPhrase, departmentId = null, companyId = null }
	) {
		const payload = buildSearchPayload(
			searchPhrase,
			companyId,
			departmentId,
			rootState.Auth.user.selectedDepartmentId
		)

		const data = await executeSimpleQuery({
			query: FetchClientsSearchSuggestions,
			variables: {
				payload,
				size: 7
			}
		})
		return data.clientSearchSuggestions?.nodes || []
	}
}
